/**
 * Environment configuration
 * Uses port-based config for development convenience + Vite env variables
 *
 * Modes:
 * - Port-based: localhost development with hardcoded restaurant IDs
 * - Subdomain: *.restohub.io — slug extracted from hostname, resolved via API
 * - Env fallback: VITE_* env variables
 */

export type AppMode = 'development' | 'subdomain' | 'embedded'

export interface EnvironmentConfig {
  apiBaseUrl: string
  restaurantId: string
  isDevelopment: boolean
  isProduction: boolean
  description: string
  mode: AppMode
  slug: string
}

/**
 * Port-based configuration for development
 */
const PORT_CONFIG: Record<string, { apiBaseUrl: string; restaurantId: string; description: string }> = {
  '6001': {
    apiBaseUrl: 'http://localhost:8000',
    restaurantId: '5a8511e0-8c44-450f-844b-98659c349346', // Local Django
    description: 'Local Dev (localhost:8000)',
  },
  '6002': {
    apiBaseUrl: 'http://localhost:8000',
    restaurantId: '5a8511e0-8c44-450f-844b-98659c349346', // Local Django
    description: 'Django API (localhost:8000)',
  },
  '6003': {
    apiBaseUrl: 'https://api.restohub.io',
    restaurantId: 'd13a32dd-730f-4114-8beb-31e258a2151a', // Production
    description: 'Production API',
  },
}

/** Reserved subdomains that are NOT restaurant slugs */
const RESERVED_SUBDOMAINS = new Set([
  'api', 'app', 'www', 'admin', 'mail', 'dns', 'cdn', 'static',
  'blog', 'docs', 'help', 'support', 'status', 'dashboard',
])

/**
 * Mutable state — set after slug resolution in subdomain mode
 */
let _resolvedRestaurantId = ''
let _resolvedSlug = ''
let _resolvedMenuSlug = ''

export function setResolvedRestaurant(restaurantId: string, slug: string) {
  _resolvedRestaurantId = restaurantId
  _resolvedSlug = slug
}

export function setResolvedMenuSlug(menuSlug: string) {
  _resolvedMenuSlug = menuSlug
}

export function getMenuSlug(): string {
  return _resolvedMenuSlug
}

/**
 * Detect subdomain slug from hostname or ?slug= query param (dev only)
 * Returns slug if on *.restohub.io (excluding reserved), empty string otherwise
 * In development, ?slug=xxx can be used to test subdomain resolution locally
 */
export function detectSubdomainSlug(): string {
  if (typeof window === 'undefined') return ''

  // Dev shortcut: ?slug=xxx on localhost triggers slug resolution against local API
  const port = window.location.port
  if (PORT_CONFIG[port]) {
    const paramSlug = new URLSearchParams(window.location.search).get('slug')
    if (paramSlug) return paramSlug.toLowerCase()
  }

  const hostname = window.location.hostname
  // Match: {slug}.restohub.io
  const match = hostname.match(/^([a-z0-9][a-z0-9-]*[a-z0-9])\.restohub\.io$/i)
  if (!match) return ''
  const slug = match[1].toLowerCase()
  if (RESERVED_SUBDOMAINS.has(slug)) return ''
  return slug
}

/**
 * Detect the current app mode
 */
export function detectMode(): AppMode {
  const port = getCurrentPort()
  if (PORT_CONFIG[port]) return 'development'
  if (detectSubdomainSlug()) return 'subdomain'
  if (import.meta.env.MODE === 'development') return 'development'
  return 'embedded'
}

/**
 * Get current port from window.location
 */
const getCurrentPort = (): string => {
  if (typeof window !== 'undefined') {
    return window.location.port
  }
  return ''
}

/**
 * Get API base URL
 */
const getApiBaseUrl = (): string => {
  const port = getCurrentPort()
  if (PORT_CONFIG[port]) {
    return PORT_CONFIG[port].apiBaseUrl
  }
  const isDev = import.meta.env.MODE === 'development'
  if (isDev) {
    return import.meta.env.VITE_BASE_URL_DEV || 'http://localhost:8000'
  }
  return import.meta.env.VITE_BASE_URL_PROD || 'https://api.restohub.io'
}

/**
 * Get restaurant ID based on environment
 * In subdomain mode, returns the resolved restaurant ID
 */
const getRestaurantId = (): string => {
  // Subdomain mode: use resolved ID
  if (_resolvedRestaurantId) return _resolvedRestaurantId

  // Query param override (used by admin preview iframe)
  if (typeof window !== 'undefined') {
    const paramId = new URLSearchParams(window.location.search).get('restaurant_id')
    if (paramId) return paramId
  }

  const port = getCurrentPort()
  if (PORT_CONFIG[port]) {
    return PORT_CONFIG[port].restaurantId
  }
  return import.meta.env.VITE_DEFAULT_RESTAURANT_ID || ''
}

/**
 * Get description for current environment
 */
const getDescription = (): string => {
  const port = getCurrentPort()
  if (PORT_CONFIG[port]) return PORT_CONFIG[port].description
  if (_resolvedSlug) return `Subdomain: ${_resolvedSlug}.restohub.io`
  return import.meta.env.MODE === 'development' ? 'Development' : 'Production'
}

/**
 * Get full environment configuration
 */
export function getEnvironmentConfig(): EnvironmentConfig {
  return {
    apiBaseUrl: getApiBaseUrl(),
    restaurantId: getRestaurantId(),
    isDevelopment: import.meta.env.MODE === 'development',
    isProduction: import.meta.env.MODE === 'production',
    description: getDescription(),
    mode: detectMode(),
    slug: _resolvedSlug || detectSubdomainSlug(),
  }
}

/**
 * Check if the widget is in preview mode (draft view from admin dashboard).
 * Triggered by ?preview=true query param — passed through to the public API
 * so the backend skips the published snapshot and returns live DB data.
 */
export function isPreviewMode(): boolean {
  if (typeof window === 'undefined') return false
  return new URLSearchParams(window.location.search).get('preview') === 'true'
}

// Convenience exports
export const isDevelopment = (): boolean => import.meta.env.MODE === 'development'
export const isProduction = (): boolean => import.meta.env.MODE === 'production'
